/**
 * Cognito Hosted UI OAuth (authorization code + PKCE).
 *
 * No dependencies — we implement just enough of the flow to:
 *   - redirect to /oauth2/authorize with a generated PKCE code verifier,
 *   - exchange the returned `code` at /oauth2/token,
 *   - store the resulting id_token / access_token in sessionStorage,
 *   - read the user's email out of the id_token's claims.
 *
 * sessionStorage was chosen over localStorage because the tokens shouldn't
 * outlive the tab; refresh-token-based silent renewal is intentionally
 * deferred to v0.2.
 */

import { config } from "./config";

const STORAGE_KEYS = {
  pkceVerifier: "ccc.pkce.verifier",
  oauthState: "ccc.oauth.state",
  idToken: "ccc.id_token",
  accessToken: "ccc.access_token",
} as const;

function randomString(bytes = 32): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return base64UrlEncode(buf);
}

function base64UrlEncode(input: Uint8Array | ArrayBuffer): string {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function sha256(input: string): Promise<ArrayBuffer> {
  return crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
}

function authBaseUrl(): string {
  return `https://${config.cognitoDomain}`;
}

/** Begin the OAuth code+PKCE flow. Redirects the browser. */
export async function login(): Promise<void> {
  const verifier = randomString(64);
  const challenge = base64UrlEncode(await sha256(verifier));
  const state = randomString(16);

  sessionStorage.setItem(STORAGE_KEYS.pkceVerifier, verifier);
  sessionStorage.setItem(STORAGE_KEYS.oauthState, state);

  const url = new URL(`${authBaseUrl()}/oauth2/authorize`);
  url.searchParams.set("client_id", config.cognitoClientId);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", "openid email profile");
  url.searchParams.set("redirect_uri", `${config.appOrigin}/callback`);
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", challenge);

  window.location.assign(url.toString());
}

/** Complete the OAuth flow. Call from /callback after Cognito redirects back. */
export async function completeLogin(searchParams: URLSearchParams): Promise<void> {
  const error = searchParams.get("error");
  if (error) {
    throw new Error(`Cognito returned error: ${error} — ${searchParams.get("error_description") ?? ""}`);
  }

  const code = searchParams.get("code");
  const returnedState = searchParams.get("state");
  const expectedState = sessionStorage.getItem(STORAGE_KEYS.oauthState);
  const verifier = sessionStorage.getItem(STORAGE_KEYS.pkceVerifier);

  if (!code) throw new Error("Callback missing authorization code.");
  if (!expectedState || returnedState !== expectedState) {
    throw new Error("OAuth state mismatch — possible CSRF.");
  }
  if (!verifier) throw new Error("Missing PKCE verifier. Restart the login.");

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.cognitoClientId,
    code,
    code_verifier: verifier,
    redirect_uri: `${config.appOrigin}/callback`,
  });

  const res = await fetch(`${authBaseUrl()}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token exchange failed: HTTP ${res.status} — ${text}`);
  }

  const json = (await res.json()) as {
    id_token?: string;
    access_token?: string;
    expires_in?: number;
  };
  if (!json.id_token || !json.access_token) {
    throw new Error("Token response missing id_token or access_token.");
  }

  sessionStorage.setItem(STORAGE_KEYS.idToken, json.id_token);
  sessionStorage.setItem(STORAGE_KEYS.accessToken, json.access_token);
  sessionStorage.removeItem(STORAGE_KEYS.pkceVerifier);
  sessionStorage.removeItem(STORAGE_KEYS.oauthState);
}

export function getIdToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEYS.idToken);
}

export function getUserEmail(): string | null {
  const token = getIdToken();
  if (!token) return null;
  try {
    const [, payloadB64] = token.split(".");
    const payload = JSON.parse(atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.email === "string" ? payload.email : null;
  } catch {
    return null;
  }
}

/** Clear local tokens and bounce through Cognito's logout endpoint. */
export function logout(): void {
  sessionStorage.removeItem(STORAGE_KEYS.idToken);
  sessionStorage.removeItem(STORAGE_KEYS.accessToken);

  const url = new URL(`${authBaseUrl()}/logout`);
  url.searchParams.set("client_id", config.cognitoClientId);
  url.searchParams.set("logout_uri", config.appOrigin);
  window.location.assign(url.toString());
}
